import React, { useState } from 'react';

interface ProfitabilityItem {
  order_id: string;
  order_number: string;
  order_item_id: string;
  item_description: string;
  width: number;
  height: number;
  selling_price: number;
  material_cost: number;
  labor_hours: number;
  labor_cost: number;
  overhead_allocated: number;
  true_cost: number;
  margin_dollars: number;
  margin_percent: number;
  has_rework: boolean;
  is_scrapped: boolean;
  calculated_at: string;
}

interface ProfitabilityTableProps {
  items: ProfitabilityItem[];
  total: number;
  loading: boolean;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (column: string) => void;
  onExportCsv: () => void;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const SortIcon = ({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) => (
  <svg
    className={`w-4 h-4 inline-block ml-1 ${active ? 'text-blue-600' : 'text-gray-400'}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    {direction === 'asc' ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    )}
  </svg>
);

export function ProfitabilityTable({
  items,
  total,
  loading,
  sortBy,
  sortDir,
  onSort,
  onExportCsv,
}: ProfitabilityTableProps) {
  const [filter, setFilter] = useState('');

  const filteredItems = items.filter((item) => {
    if (!filter) return true;
    const searchLower = filter.toLowerCase();
    return (
      item.order_number?.toLowerCase().includes(searchLower) ||
      item.item_description?.toLowerCase().includes(searchLower)
    );
  });

  const SortableHeader = ({ column, children }: { column: string; children: React.ReactNode }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon active={sortBy === column} direction={sortBy === column ? sortDir : 'desc'} />
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Profitability Details</h3>
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Profitability Details</h3>
          <p className="text-sm text-gray-500">{total.toLocaleString()} items total</p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Filter by order or description..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={onExportCsv}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Size
              </th>
              <SortableHeader column="selling_price">Revenue</SortableHeader>
              <SortableHeader column="material_cost">Material</SortableHeader>
              <SortableHeader column="labor_cost">Labor</SortableHeader>
              <SortableHeader column="overhead_allocated">Overhead</SortableHeader>
              <SortableHeader column="true_cost">True Cost</SortableHeader>
              <SortableHeader column="margin_dollars">Margin $</SortableHeader>
              <SortableHeader column="margin_percent">Margin %</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Flags
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  No data available
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <a
                      href={`/orders/${item.order_id}`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {item.order_number || '-'}
                    </a>
                    {item.item_description && (
                      <p className="text-xs text-gray-500 truncate max-w-xs">{item.item_description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {item.width}" x {item.height}"
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.selling_price)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(item.material_cost)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(item.labor_cost)}
                    <span className="text-xs text-gray-400 ml-1">
                      ({item.labor_hours.toFixed(2)}h)
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(item.overhead_allocated)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(item.true_cost)}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                    item.margin_dollars >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(item.margin_dollars)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.margin_percent >= 25
                        ? 'bg-green-100 text-green-800'
                        : item.margin_percent >= 15
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.margin_percent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      {item.has_rework && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          Rework
                        </span>
                      )}
                      {item.is_scrapped && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Scrapped
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProfitabilityTable;
